import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "../ModalBasicLayout";
import SubtitlePreview from "./SubtitlePreview";
import Select from "../../Form/Select";
import Switch from "../../Form/Switch";
import ColorField from "../../Form/ColorField";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import {
  MODAL_SUBTITLE_OPTIONS_TITLE,
  MODAL_SUBTITLE_OPTIONS_FONT_FAMILY,
  MODAL_SUBTITLE_OPTIONS_FONT_STYLE,
  MODAL_SUBTITLE_OPTIONS_SUBTITLE_COLOR,
  MODAL_SUBTITLE_OPTIONS_DO_HIGHLIGHT,
  MODAL_SUBTITLE_OPTIONS_HIGHLIGHT_COLOR,
  MODAL_SUBTITLE_OPTIONS_HIGHLIGHT_MODE,
  // MODAL_SUBTITLE_OPTIONS_HAS_BACKGROUND,
  // MODAL_SUBTITLE_OPTIONS_BACKGROUND_COLOR,
  MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL,
  BUTTON_SAVE,
  BUTTON_CANCEL,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { startSaveSubtitlesConfiguration } from "../../../actions/user";

const SUBTITLE_FONT_OPTIONS = [
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Inter", label: "Inter" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Roboto Mono", label: "Roboto Mono" },
  { value: "Oswald", label: "Oswald" },
  { value: "Roboto Slab", label: "Roboto Slab" },
  { value: "Bebas Neue", label: "Bebas Neue" },
  { value: "Titillium Web", label: "Titillium Web" },
  { value: "Source Code Pro", label: "Source Code Pro" },
  { value: "Indie Flower", label: "Indie Flower" },
];

/** Fuentes ya cargadas para no inyectar el <link> más de una vez */
const loadedGoogleFonts = new Set();

/**
 * Carga una fuente de Google Fonts bajo demanda (solo cuando se usa en este componente).
 * No carga todas las fuentes al inicio de la página.
 */
const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || loadedGoogleFonts.has(fontFamily)) return;
  loadedGoogleFonts.add(fontFamily);
  const familyParam = fontFamily.replace(/ /g, "+");
  const url = `https://fonts.googleapis.com/css2?family=${familyParam}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.setAttribute("data-google-font", fontFamily);
  document.head.appendChild(link);
};

const SUBTITLE_FONT_STYLE_OPTIONS = [
  { value: "normal", label: "modalSubtitleOptionsFontStyleNormal" },
  { value: "bold", label: "modalSubtitleOptionsFontStyleBold" },
  { value: "italic", label: "modalSubtitleOptionsFontStyleItalic" },
  { value: "bold_italic", label: "modalSubtitleOptionsFontStyleBoldItalic" },
];

const SUBTITLE_HIGHLIGHT_MODE_OPTIONS = [
  { value: "karaoke", label: "modalSubtitleOptionsHighlightModeKaraoke" },
  { value: "word", label: "modalSubtitleOptionsHighlightModeWord" },
];

const DEFAULT_VALUES = {
  fontFamily: "Open Sans",
  fontStyle: "bold",
  subtitleColor: "#FFFFFF",
  doHighlight: true,
  highlightColor: "#00FFBF",
  highlightMode: "word",
  hasBackground: false,
  backgroundColor: "#000000",
};

const ModalSubtitleOptions = ({
  modalOpen,
  onCloseModal,
  initialValues,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslationApp();
  const { control, handleSubmit, watch, reset, setValue, formState } = useForm({
    defaultValues: { ...DEFAULT_VALUES, ...initialValues },
    mode: "onChange",
  });
  const dispatch = useDispatchApp();
  const watchedValues = watch();
  const doHighlight = watchedValues.doHighlight;
  // const hasBackground = watchedValues.hasBackground;
  const { errors } = formState;

  useEffect(() => {
    if (modalOpen) {
      reset({ ...DEFAULT_VALUES, ...initialValues });
    }
  }, [modalOpen, initialValues, reset]);

  // Cargar la fuente de Google Fonts solo cuando está abierto el modal y hay una selección
  useEffect(() => {
    if (modalOpen && watchedValues.fontFamily) {
      loadGoogleFont(watchedValues.fontFamily);
    }
  }, [modalOpen, watchedValues.fontFamily]);

  const onSubmit = async (data) => {
    setIsSaving(true);
    await dispatch(startSaveSubtitlesConfiguration(data));
    setIsSaving(false);
    onSave(data);
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      maxWidth="sm"
      scroll="paper"
      title={t(MODAL_SUBTITLE_OPTIONS_TITLE)}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Select
          name="fontFamily"
          control={control}
          variant="filled"
          options={SUBTITLE_FONT_OPTIONS}
          label={t(MODAL_SUBTITLE_OPTIONS_FONT_FAMILY)}
          fullWidth
          size="small"
          errors={errors}
          rules={{ required: false }}
        />

        <Select
          name="fontStyle"
          control={control}
          variant="filled"
          options={SUBTITLE_FONT_STYLE_OPTIONS}
          label={t(MODAL_SUBTITLE_OPTIONS_FONT_STYLE)}
          fullWidth
          size="small"
          doTranslate={true}
          errors={errors}
          rules={{ required: false }}
        />

        <ColorField
          control={control}
          name="subtitleColor"
          label={t(MODAL_SUBTITLE_OPTIONS_SUBTITLE_COLOR)}
          fullWidth
          size="small"
          errors={errors}
          rules={{ required: false }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 600, fontSize: 14, color: "text.primary" }}
          >
            {t(MODAL_SUBTITLE_OPTIONS_DO_HIGHLIGHT)}
          </Box>
          <Switch
            id="do-highlight-switch"
            isActive={!!doHighlight}
            size="small"
            hasLabel={false}
            onChangeIsActive={(checked) => setValue("doHighlight", checked)}
          />
        </Box>

        {doHighlight && (
          <>
            <ColorField
              control={control}
              name="highlightColor"
              label={t(MODAL_SUBTITLE_OPTIONS_HIGHLIGHT_COLOR)}
              fullWidth
              size="small"
              errors={errors}
              rules={{ required: false }}
            />
            <Select
              name="highlightMode"
              control={control}
              variant="filled"
              options={SUBTITLE_HIGHLIGHT_MODE_OPTIONS}
              label={t(MODAL_SUBTITLE_OPTIONS_HIGHLIGHT_MODE)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
              rules={{ required: false }}
            />
          </>
        )}

        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 600, fontSize: 14, color: "text.primary" }}
          >
            {t(MODAL_SUBTITLE_OPTIONS_HAS_BACKGROUND)}
          </Box>
          <Switch
            id="has-background-switch"
            isActive={!!hasBackground}
            size="small"
            hasLabel={false}
            onChangeIsActive={(checked) => setValue("hasBackground", checked)}
          />
        </Box>

        {hasBackground && (
          <ColorField
            control={control}
            name="backgroundColor"
            label={t(MODAL_SUBTITLE_OPTIONS_BACKGROUND_COLOR)}
            fullWidth
            size="small"
            errors={errors}
            rules={{ required: false }}
          />
        )} */}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL)}
          </Typography>
          <SubtitlePreview options={watchedValues} />
        </Box>
      </Box>

      <DialogActions sx={{ px: 0, pt: 2, pb: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_CANCEL,
          }}
          propsPrimaryButton={{
            showButton: true,
            onClick: handleSubmit(onSubmit),
            text: BUTTON_SAVE,
            type: "button",
            loading: isSaving,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalSubtitleOptions;
